<template>
  <div>
    <ts-page-title
      :title="pageTitle"
      :breadcrumb="[
        { text: $t('home'), href: '/' },
        { text: $t('telegramMessage.historyPageTitle'), href: '/admin/notification/telegram-messages' },
        { text: pageTitle, active: true }
      ]"
    />
    <ts-panel class="telegram-compose-panel">
      <a-spin :spinning="pageLoading || loading" class="telegram-panel-spin">
        <ts-panel-wrapper>
          <div class="telegram-compose-grid">
            <telegram-message-form
              v-model="model"
              :form-models="formModels"
              :loading="loading"
              :title="pageTitle"
              :scheduled-at.sync="scheduledAt"
              :image-preview="imagePreview"
              @image-selected="setImagePreview"
              @image-cleared="clearImagePreview"
              @save-draft="saveDraft"
              @schedule="schedule"
              @submit="submit"
              @cancel="goBack"
            />
            <div class="telegram-preview-column">
              <telegram-message-preview :model="model" :image-preview="imagePreview" />
            </div>
          </div>
        </ts-panel-wrapper>
      </a-spin>
    </ts-panel>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import moment from 'moment'
import TelegramMessageForm from './components/TelegramMessageForm.vue'
import TelegramMessagePreview from './components/TelegramMessagePreview.vue'
import {
  buildTelegramMessageFormData,
  defaultTelegramMessageModel,
  draftToTelegramMessageModel
} from './components/form-data'

export default {
  name: 'telegramMessageEdit',
  components: {
    TelegramMessageForm,
    TelegramMessagePreview
  },
  data: () => ({
    model: defaultTelegramMessageModel(),
    imagePreview: null,
    scheduledAt: '',
    loadingDraft: false,
    pageLoading: true
  }),
  computed: {
    ...mapState('notification/telegramMessage', ['formModels', 'loading']),
    draftId () {
      return this.$route.params.id
    },
    pageTitle () {
      return this.$t('telegramMessage.editDraft')
    }
  },
  created () {
    this.pageLoading = true
    this.getFormModels().then(this.loadDraft).catch(error => {
      this.$notify({ type: 'error', text: error.message || this.$t('telegramMessage.formModelsLoadFailed') })
      this.goBack()
    }).finally(() => {
      this.pageLoading = false
    })
  },
  beforeDestroy () {
    this.revokeImagePreview()
  },
  methods: {
    ...mapActions('notification/telegramMessage', ['getFormModels', 'findDraft', 'updateDraft']),
    goBack () {
      this.$router.push({ name: 'telegram-message' })
    },
    loadDraft () {
      this.loadingDraft = true
      return this.findDraft(this.draftId).then(draft => {
        this.model = draftToTelegramMessageModel(draft)
        this.imagePreview = draft.image_path || null
        this.scheduledAt = this.formatScheduleDate(draft.scheduled_at)
      }).catch(error => {
        this.$notify({ type: 'error', text: error.message || this.$t('telegramMessage.draftNotFound') })
        this.goBack()
      }).finally(() => {
        this.loadingDraft = false
      })
    },
    setImagePreview (file) {
      this.revokeImagePreview()
      this.imagePreview = URL.createObjectURL(file)
    },
    clearImagePreview () {
      this.revokeImagePreview()
      this.imagePreview = null
    },
    revokeImagePreview () {
      if (this.imagePreview && this.imagePreview.indexOf('blob:') === 0) {
        URL.revokeObjectURL(this.imagePreview)
      }
    },
    formatScheduleDate (value) {
      return value ? moment(value).format('YYYY-MM-DD HH:mm') : ''
    },
    saveDraft () {
      this.updateDraft({
        id: this.draftId,
        data: buildTelegramMessageFormData(this.model, 'draft', this.scheduledAt)
      }).then(() => {
        this.$notify({ type: 'success', text: this.$t('telegramMessage.draftSaved') })
        this.goBack()
      }).catch(error => this.$notify({ type: 'error', text: error.message }))
    },
    schedule () {
      this.updateDraft({
        id: this.draftId,
        data: buildTelegramMessageFormData(this.model, 'schedule', this.scheduledAt)
      }).then(response => {
        this.$notify({ type: 'success', text: response.message + ' (' + response.recipient_count + ')' })
        this.goBack()
      }).catch(error => this.$notify({ type: 'error', text: error.message }))
    },
    submit () {
      this.updateDraft({
        id: this.draftId,
        data: buildTelegramMessageFormData(this.model, 'send')
      }).then(response => {
        this.$notify({ type: 'success', text: response.message + ' (' + response.recipient_count + ')' })
        this.goBack()
      }).catch(error => this.$notify({ type: 'error', text: error.message }))
    }
  }
}
</script>

<style scoped>
.telegram-compose-panel { background: transparent !important; box-shadow: none !important; }
.telegram-compose-panel >>> .card-body { padding: 0; }
.telegram-panel-spin { display: block; }
.telegram-panel-spin >>> .ant-spin { max-height: none; }
.telegram-panel-spin >>> .ant-spin-spinning { align-items: center; display: flex; justify-content: center; }
.telegram-compose-grid { display: grid; gap: 24px; grid-template-columns: minmax(0, 7fr) minmax(320px, 5fr); margin: 0; }
.telegram-preview-column { display: flex; margin-top: 0 !important; padding-left: 0; }
@media (max-width: 991px) {
  .telegram-compose-grid { grid-template-columns: 1fr; }
}
</style>
